import { z } from 'zod';
import { Id } from './primitives.js';
/**
 * The PGD SFA field questionnaire — from the client's "PGD SFA Questionaire"
 * workbook.
 *
 * A salesperson answers a short structured survey during a visit, alongside the
 * photographs the vision pipeline reads. The QUESTIONS are master data the
 * console maintains; the ANSWERS are visit data recorded in the field and scoped
 * like every other visit fact.
 *
 * The workbook has two sheets — On Trade and Off Trade — and groups the questions
 * under four retail-execution pillars. Both become columns on the question rather
 * than tables of their own: a question belongs to exactly one sheet and one
 * pillar, and neither is a value an administrator invents at runtime.
 */
/**
 * Which sheet a question belongs to.
 *
 * ON_TRADE is on-premise consumption — bars, restaurants, cafés (the HORECA
 * channel). OFF_TRADE is take-home retail (GT and MT). The salesperson CHOOSES
 * one at check-in, so the pick is recorded on the visit's questionnaire rather
 * than derived from the outlet's channel — the client asked for an explicit
 * choice rather than an inference that could silently disagree with the master.
 */
export const QuestionnaireTradeType = z.enum(['ON_TRADE', 'OFF_TRADE']);
/**
 * The four pillars the workbook groups questions under. A fixed vocabulary, not
 * administrable — the survey is built around exactly these four.
 *
 * RIGHT_PRICE is kept even though the workbook currently marks its questions
 * BATAL (cancelled): the pillar is part of the structure, and a question may be
 * added back under it later. A category with no active questions simply renders
 * nothing.
 */
export const QuestionnaireCategory = z.enum([
    'AVAILABILITY',
    'VISIBILITY',
    'RIGHT_PRICE',
    'ACTIVATION',
]);
/**
 * How a question is answered — the workbook's "answer" column.
 *
 *   FREE_TEXT   — a typed note or figure ("isi brand apa", "nominal angka").
 *   YES_NO      — a boolean. May ALSO carry `options` for the "if Yes, which
 *                 brand" follow-up the workbook attaches to several questions.
 *   MULTI_CHECK — tick any number of `options` (the brand checklists).
 *   DROPDOWN    — pick exactly one of `options` (the competitor lists).
 */
export const QuestionnaireAnswerType = z.enum(['FREE_TEXT', 'YES_NO', 'MULTI_CHECK', 'DROPDOWN']);
/**
 * One selectable choice for a MULTI_CHECK or DROPDOWN question.
 *
 * `code` is the stable handle an answer records; `label` is what the field app
 * shows. An answer SNAPSHOTS the chosen options rather than pointing at them, so
 * editing or retiring a choice later never rewrites what a salesperson reported.
 */
export const QuestionnaireOption = z.object({
    code: z.string().min(1).max(64),
    label: z.string().min(1).max(200),
});
export const QuestionnaireQuestion = z.object({
    id: Id,
    /** The natural key — the workbook's row handle, stable across a rename. */
    code: z.string().min(1).max(64),
    tradeType: QuestionnaireTradeType,
    category: QuestionnaireCategory,
    /**
     * An optional sub-heading within a pillar — the GOLD / SILVER split the Off
     * Trade Activation section uses. Null where the pillar has no sub-heading.
     */
    section: z.string().max(100).nullable(),
    /** The question text shown to the salesperson. */
    prompt: z.string().min(1).max(500),
    answerType: QuestionnaireAnswerType,
    /** The workbook's "Photo Required?" column. */
    photoRequired: z.boolean(),
    /** Empty for FREE_TEXT and YES_NO; the choices for MULTI_CHECK and DROPDOWN. */
    options: z.array(QuestionnaireOption),
    /** Ordering within a (tradeType, category) group. */
    sortOrder: z.number().int().nonnegative(),
    /**
     * The optional example image an admin attached (0072) — the raw object key,
     * kept so the console form can round-trip it on save. Null when there is none.
     */
    exampleImageObjectKey: z.string().nullable(),
    /**
     * A short-lived presigned GET for that image, signed per read — what the field
     * app and the console preview actually render. Null when there is no image.
     */
    exampleImageUrl: z.string().url().nullable(),
    active: z.boolean(),
});
/* -------------------------------------------------------------------------- */
/* Write requests — question master data                                      */
/* -------------------------------------------------------------------------- */
/**
 * `options` and the answer type have to agree: MULTI_CHECK and DROPDOWN are
 * unanswerable without choices, FREE_TEXT must carry none, and YES_NO may carry
 * them (the workbook's "if Yes, which brand" follow-up) or not.
 */
const optionsMatchAnswerType = (answerType, optionCount) => {
    if (answerType === 'FREE_TEXT')
        return optionCount === 0;
    if (answerType === 'MULTI_CHECK' || answerType === 'DROPDOWN')
        return optionCount > 0;
    return true; // YES_NO — options are an optional follow-up.
};
export const CreateQuestionnaireQuestionRequest = z
    .object({
    code: z.string().min(1).max(64),
    tradeType: QuestionnaireTradeType,
    category: QuestionnaireCategory,
    section: z.string().max(100).trim().nullable().default(null),
    prompt: z.string().min(1).max(500),
    answerType: QuestionnaireAnswerType,
    photoRequired: z.boolean().default(false),
    options: z.array(QuestionnaireOption).max(50).default([]),
    sortOrder: z.number().int().nonnegative().default(0),
    /** The optional example image's object key, from the presign step; null for none. */
    exampleImageObjectKey: z.string().min(1).max(500).nullable().default(null),
})
    .refine((body) => optionsMatchAnswerType(body.answerType, body.options.length), {
    message: 'MULTI_CHECK/DROPDOWN wajib punya opsi; FREE_TEXT tidak boleh; YES_NO boleh dengan atau tanpa opsi',
    path: ['options'],
});
/**
 * Partial update. `code` is absent for the reason it is absent from every other
 * master update here: it is the natural key, and renaming it would strand the
 * answers already recorded against the question.
 */
export const UpdateQuestionnaireQuestionRequest = z
    .object({
    tradeType: QuestionnaireTradeType,
    category: QuestionnaireCategory,
    section: z.string().max(100).trim().nullable(),
    prompt: z.string().min(1).max(500),
    answerType: QuestionnaireAnswerType,
    photoRequired: z.boolean(),
    options: z.array(QuestionnaireOption).max(50),
    sortOrder: z.number().int().nonnegative(),
    /**
     * The example image's object key (0072). `.partial()` makes it omittable
     * (leave as-is); `null` clears it, a value sets it — the console form sends
     * its current value so an edit that does not touch the image preserves it.
     */
    exampleImageObjectKey: z.string().min(1).max(500).nullable(),
})
    .partial();
/* -------------------------------------------------------------------------- */
/* Category master — the administrable pillar list                             */
/* -------------------------------------------------------------------------- */
/**
 * The questionnaire's CATEGORY master — write shape.
 *
 * `QuestionnaireCategory` above is the four fixed pillars the workbook shipped
 * with. The client asked for that axis to be administrable instead — its own
 * master and its own console screen — so an operator can add, rename or retire a
 * category without a migration. The question's `category` column still reads the
 * fixed enum for now; repointing it at this master is a later, separate change.
 *
 * NAME ONLY, no code, exactly like `UpsertSkuCategoryRequest` and for the same
 * reason: a questionnaire category is an internal label nothing outside this
 * system quotes, so a second natural key would only be something an importer
 * could disagree with itself about. A rename therefore propagates by reference.
 * Reactivation/retirement reuses the shared `SetActiveRequest`.
 */
export const UpsertQuestionnaireCategoryRequest = z.object({ name: z.string().min(1).max(100) });
/* -------------------------------------------------------------------------- */
/* Field submission — the salesperson's answers                               */
/* -------------------------------------------------------------------------- */
/**
 * One answer the field app submits. Exactly one value field is populated,
 * matching the question's answer type; the rest are null. `selectedOptions`
 * carries option CODES — the server snapshots their labels from the question at
 * submit time so the stored answer survives a later edit to the choice list.
 */
export const QuestionnaireAnswerInput = z.object({
    questionId: Id,
    textValue: z.string().max(2000).nullable().default(null),
    boolValue: z.boolean().nullable().default(null),
    selectedOptions: z.array(z.string().min(1).max(64)).max(50).nullable().default(null),
    /**
     * The object-storage key of the evidence photo, when the question asked for one
     * ("jika Yes difoto"). Uploaded straight from the app to a presigned URL under
     * the `questionnaire/<visit>/…` prefix; the server validates that prefix before
     * storing it.
     */
    photoObjectKey: z.string().min(1).max(500).nullable().default(null),
});
/* -------------------------------------------------------------------------- */
/* Evidence photo upload — presign                                            */
/* -------------------------------------------------------------------------- */
/**
 * Step 1 of attaching an evidence photo: ask for somewhere to put it. Same
 * three-step shape as a visit photo and a SKU image — the image goes straight to
 * object storage, never through the api. `contentLength` is bound into the
 * signature so the URL cannot be reused to upload something else.
 */
export const PresignQuestionnairePhotoRequest = z.object({
    contentType: z.enum(['image/jpeg', 'image/webp', 'image/png']),
    contentLength: z.number().int().positive().max(10 * 1024 * 1024),
});
export const PresignQuestionnairePhotoResponse = z.object({
    uploadUrl: z.string().url(),
    objectKey: z.string().min(1),
    expiresAt: z.string().datetime(),
});
/**
 * Presign an upload for a question's example image (0072) — the console's
 * version of the evidence-photo presign above. Same three-step shape (presign →
 * PUT straight to storage → store the key on the question), no visit context:
 * the image illustrates a master-data question, not a visit.
 */
export const PresignQuestionnaireExampleImageRequest = z.object({
    contentType: z.enum(['image/jpeg', 'image/webp', 'image/png']),
    contentLength: z.number().int().positive().max(10 * 1024 * 1024),
});
export const PresignQuestionnaireExampleImageResponse = z.object({
    uploadUrl: z.string().url(),
    objectKey: z.string().min(1),
    expiresAt: z.string().datetime(),
});
/**
 * Submit (or re-submit) the whole questionnaire for a visit. Idempotent on
 * (visit, question): each answer upserts, so the app may send the full set again
 * after the salesperson changes one.
 */
export const SubmitVisitQuestionnaireRequest = z.object({
    tradeType: QuestionnaireTradeType,
    answers: z.array(QuestionnaireAnswerInput).max(200),
});
/* -------------------------------------------------------------------------- */
/* Sheet resolver — which sheet applies to this visit's outlet                */
/* -------------------------------------------------------------------------- */
/**
 * `GET /questionnaire/visits/:visitId/sheet` — the field app's "which sheet
 * applies here?" lookup.
 *
 * The salesperson no longer picks On Trade / Off Trade. It is DERIVED from the
 * visited outlet's classification (`OutletClassification.tradeType`), and this
 * is what the app reads to know which questions to load. `tradeType` is null
 * when the classification has not been marked yet — the app then shows a short
 * notice instead of a questionnaire. `classificationName` names it in that
 * notice.
 */
export const VisitQuestionnaireSheet = z.object({
    tradeType: QuestionnaireTradeType.nullable(),
    classificationName: z.string(),
    /** Point 7: whether filling the questionnaire is required before check-out (the global toggle). */
    required: z.boolean(),
    /** Whether this visit already has a submitted questionnaire. */
    submitted: z.boolean(),
});
/* -------------------------------------------------------------------------- */
/* Questionnaire settings — the global required-at-checkout toggle (Point 7)   */
/* -------------------------------------------------------------------------- */
/**
 * The one administrable questionnaire setting: whether the field app must have a
 * questionnaire before a visit can be checked out. Lives on the Kuesioner master
 * screen (a toggle), read by the check-out guard and the app. A single boolean —
 * kept as its own object so adding a second questionnaire-wide setting later does
 * not reshape the endpoint.
 */
export const QuestionnaireSettings = z.object({ requiredAtCheckout: z.boolean() });
export const UpdateQuestionnaireSettingsRequest = z.object({ requiredAtCheckout: z.boolean() });
/* -------------------------------------------------------------------------- */
/* Read models — the console's view of what was answered                      */
/* -------------------------------------------------------------------------- */
/**
 * An answer read back for the console, the question's display fields
 * denormalised beside the recorded value — the same shape `Outlet` uses for its
 * channel name, so a reader renders a row without a second lookup.
 */
export const VisitQuestionnaireAnswerView = z.object({
    questionId: Id,
    code: z.string(),
    category: QuestionnaireCategory,
    section: z.string().nullable(),
    prompt: z.string(),
    answerType: QuestionnaireAnswerType,
    photoRequired: z.boolean(),
    textValue: z.string().nullable(),
    boolValue: z.boolean().nullable(),
    /** The snapshot taken at submit time — labels as they read then. */
    selectedOptions: z.array(QuestionnaireOption).nullable(),
    /** Short-lived presigned GET for the evidence photo, when one was attached. */
    photoUrl: z.string().url().nullable(),
});
export const VisitQuestionnaireView = z.object({
    visitId: Id,
    tradeType: QuestionnaireTradeType,
    answers: z.array(VisitQuestionnaireAnswerView),
});
/* -------------------------------------------------------------------------- */
/* Console list — one row per submitted questionnaire                         */
/* -------------------------------------------------------------------------- */
/**
 * One row of the "Hasil Kuesioner" report — a questionnaire a salesperson sent.
 *
 * Its own console surface, standalone from the visit report: a questionnaire
 * recorded on a visit that never pressed "Kirim data" has no visit-report row to
 * hang off, so the answers would be unreachable if they only showed in the visit
 * detail. This list reads the questionnaires directly instead.
 */
export const QuestionnaireResponseRow = z.object({
    visitId: Id,
    checkInAt: z.string().datetime(),
    salesUserName: z.string(),
    outletName: z.string(),
    outletCode: z.string(),
    regionName: z.string(),
    tradeType: QuestionnaireTradeType,
    answerCount: z.number().int().nonnegative(),
});
//# sourceMappingURL=questionnaire.js.map